import Analytics, { Events } from 'analytics/Analytics'
import { EventPopoverProvider } from 'components/calendar/eventPopover/EventPopoverProvider'
import GuestMode from 'components/guest/GuestMode'
import LoginPage, { PhoneType } from 'components/login/LoginPage'
import { getDefaultTimezone, setDefaultTimezone } from 'index'
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState
} from 'react'
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams
} from 'react-router-dom'
import Database from 'services/db/Database'
import LocalStorage from 'services/LocalStorage'
import Logger from 'services/Logger'
import { LocalStorageKey } from 'types/services/localStorage'
import { fetchAndCacheTiers } from 'utils/api/tiers'
import { isCypress } from 'utils/cypress'
import { getOrUpdateDeviceId } from 'utils/device'
import { isTestEnv } from 'utils/env'
import { isGuest, isPreviewMode } from 'utils/user'

import { useCalendar } from './CalendarProvider'
import { EventActionsProvider } from './EventActionsProvider'
import { doGetChanges } from './GetChangesProvider'
import { useOutlookContext } from './outlook/OutlookProvider'

export enum CodeProvider {
  Firebase = 'firebase',
  Checkmobi = 'checkmobi'
}

const DEFAULT_CODE_PROVIDER = CodeProvider.Checkmobi

/**
 * PROVIDER_OVERRIDE in local storage wins over the default provider,
 * in every environment. Anything else falls back to the default.
 */
export function resolveCodeProvider(): CodeProvider {
  const override = LocalStorage.get(LocalStorageKey.PROVIDER_OVERRIDE)
  return override === CodeProvider.Firebase ||
    override === CodeProvider.Checkmobi
    ? (override as CodeProvider)
    : DEFAULT_CODE_PROVIDER
}

interface AuthProviderProps {
  children: ReactNode
}

export interface AuthContextProps {
  onLogout: () => Promise<void> | undefined
  isAuth: boolean
  firstGetChangesFinished: boolean
  phoneType: PhoneType
  onPhoneType: (phoneType: PhoneType) => void
  codeProvider: CodeProvider
  onCodeProvider: (codeProvider: CodeProvider) => void
  isPreview: boolean
  syncAppWithServer: () => void
  phoneTypeUpdateFlow: boolean
  onPhoneTypeUpdateFlow: (b: boolean) => void
  showLandlineDesc: boolean
  setShowLandlineDesc: (show: boolean) => void
}

const AuthContext = createContext<AuthContextProps>({
  isAuth: false,
  firstGetChangesFinished: false,
  onLogout: function (): Promise<void> | undefined {
    return undefined
  },
  phoneType: PhoneType.Mobile,
  onPhoneType: () => {},
  codeProvider: DEFAULT_CODE_PROVIDER,
  onCodeProvider: () => {},
  isPreview: false,
  syncAppWithServer: () => {},
  phoneTypeUpdateFlow: false,
  onPhoneTypeUpdateFlow: () => {},
  showLandlineDesc: false,
  setShowLandlineDesc: () => {}
})

export default function AuthProvider(props: AuthProviderProps): JSX.Element {
  const [phoneTypeUpdateFlow, onPhoneTypeUpdateFlow] = useState(false)
  const [phoneType, onPhoneType] = useState<PhoneType>(PhoneType.Mobile)
  const [firstGetChangesFinished, onFirstGetChangesFinished] = useState(
    LocalStorage.get(LocalStorageKey.CLEAN_GET_CHANGES_DONE) != null || false
  )
  const [isAuth, onAuth] = useState(
    !!LocalStorage.get(LocalStorageKey.ACCESS_TOKEN)
  )

  useEffect(() => {
    onPhoneType(PhoneType.Mobile)
  }, [phoneTypeUpdateFlow])

  // Inside your component:
  const navigate = useNavigate()

  // checking embed group ids in url
  const location = useLocation()
  const queryParams = new URLSearchParams(location.search)
  const groupIds = queryParams.get('groupIds')?.split(',') || []
  const calendarTitle = queryParams.get('calendarTitle') ?? ''
  const isPreview = isPreviewMode()

  const calendar = useCalendar()
  const params = useParams()
  const [searchParams, onSearchParams] = useSearchParams()
  const [codeProvider, onCodeProvider] =
    useState<CodeProvider>(resolveCodeProvider)

  useEffect(() => {
    LocalStorage.set(LocalStorageKey.CODE_PROVIDER, codeProvider)
  }, [codeProvider])

  const compositeGroupIdAndPass = params.groupIdAndPass

  Logger.pink('Link:', compositeGroupIdAndPass)

  const parts = compositeGroupIdAndPass?.includes('|')
    ? (compositeGroupIdAndPass?.split('|') ?? [])
    : (compositeGroupIdAndPass?.split('⁠') ?? [])

  Logger.pink('Link:Parts:', parts)

  const groupId = parts[0]
  const password = parts[1]

  let mobileLink = ''

  if (parts.length > 2) {
    mobileLink = parts[2]
    LocalStorage.set(LocalStorageKey.MOBILE_LINK, mobileLink)
  }

  Analytics.getInstance({
    amplitudeDeviceId: searchParams.get('aid'),
    singularDeviceId: searchParams.get('sid'),
    clevertapDeviceId: searchParams.get('cid')
  })
  if (groupId) {
    /**
     * This happens only when user pressed on a dynamic link to join/lookup a group
     * we need to cache this id in case of user not logged in
     * or launch join group in case of user logged in
     */

    LocalStorage.set(LocalStorageKey.CACHED_GROUP_ID_JOIN, groupId)
    LocalStorage.set(LocalStorageKey.CACHED_GROUP_PASSWORD, password)

    // if user already logged in, we need to try join group
    if (LocalStorage.get(LocalStorageKey.USER_ID) && !isPreview && !isGuest()) {
      navigate('/', { replace: true })
    }
  }

  function setCodeProvider(provider: CodeProvider) {
    if (
      LocalStorage.get(LocalStorageKey.PROVIDER_OVERRIDE) ===
      CodeProvider.Firebase
    ) {
      onCodeProvider(CodeProvider.Firebase)
      LocalStorage.set(LocalStorageKey.CODE_PROVIDER, CodeProvider.Firebase)
      return
    }

    onCodeProvider(provider)
    LocalStorage.set(LocalStorageKey.CODE_PROVIDER, provider)
  }

  const outlookContext = useOutlookContext()

  function syncAppWithServer() {
    doGetChanges(
      LocalStorage.get(LocalStorageKey.CLEAN_GET_CHANGES_DONE)
        ? LocalStorage.get(LocalStorageKey.DEVICE_ID)
        : null,
      undefined,
      LocalStorage.get(LocalStorageKey.LAST_UPDATE),
      false,
      outlookContext
    ).then(() => {
      LocalStorage.set(LocalStorageKey.CLEAN_GET_CHANGES_DONE, true)
      onFirstGetChangesFinished(true)
      if (outlookContext.verifyCalendarConnection)
        outlookContext.verifyCalendarConnection()

      if (!LocalStorage.get(LocalStorageKey.SAW_MAIN_SCREEN) && isAuth) {
        LocalStorage.set(LocalStorageKey.SAW_MAIN_SCREEN, 1)
        Analytics.getInstance().sendEvent(Events.FIRST_MAIN_SCREEN)
      }
    })
  }

  useEffect(() => {
    if (isAuth) {
      syncAppWithServer()
      fetchAndCacheTiers()
    }

    // once per application load set a default timezone
    const tz = getDefaultTimezone()
    const current = LocalStorage.get(LocalStorageKey.TIMEZONE)

    if (current === undefined || current === null || current !== tz) {
      setDefaultTimezone(tz)
    }
  }, [isAuth])

  const onLogout = async () => {
    calendar.onGroupServer('')
    // Set auth to false first to unmount components observing the database
    onPhoneType(PhoneType.Mobile)
    onCodeProvider(resolveCodeProvider())
    onAuth(false)
    onFirstGetChangesFinished(false)

    // Defer database reset to allow React to unmount observing components
    const logout = await processLogout()
    return logout
  }

  window.processLogout = onLogout

  const [showLandlineDesc, setShowLandlineDesc] = useState(false)

  useEffect(() => {
    setShowLandlineDesc(phoneType === PhoneType.Landline)
  }, [phoneType])

  if (
    isPreview ||
    (!isAuth && ((groupId && groupId.length > 0) || groupIds.length > 0))
  ) {
    return (
      <EventActionsProvider>
        <EventPopoverProvider>
          <GuestMode
            groupId={groupId}
            groupsIds={groupIds}
            calendarTitle={calendarTitle}
            preview={isPreview || isGuest()}
          />
        </EventPopoverProvider>
      </EventActionsProvider>
    )
  }

  const children =
    !isAuth || phoneTypeUpdateFlow ? (
      <LoginPage isAuth={isAuth} onAuth={onAuth} />
    ) : (
      (props.children as JSX.Element)
    )

  return (
    <AuthContext.Provider
      value={{
        onLogout,
        isAuth,
        firstGetChangesFinished,
        phoneType,
        onPhoneType,
        codeProvider,
        onCodeProvider: setCodeProvider,
        isPreview,
        syncAppWithServer,
        phoneTypeUpdateFlow,
        onPhoneTypeUpdateFlow,
        showLandlineDesc,
        setShowLandlineDesc
      }}
    >
      <>{children}</>
    </AuthContext.Provider>
  )
}

export async function processLogout() {
  const tfmPass = LocalStorage.get(LocalStorageKey.PASSWORD)
  const themeMode = LocalStorage.get(LocalStorageKey.THEME_MODE)
  const deviceuuid = getOrUpdateDeviceId()
  const expiryState = LocalStorage.get(LocalStorageKey.EXPIRY_DIALOG_SHOWN)
  const lang = LocalStorage.get('i18nextLng')
  const host = LocalStorage.get(LocalStorageKey.APP_HOST)
  const provider = LocalStorage.get(LocalStorageKey.CODE_PROVIDER)
  const providerOverride = LocalStorage.get(LocalStorageKey.PROVIDER_OVERRIDE)
  const cachedGroupId = LocalStorage.get(LocalStorageKey.CACHED_GROUP_ID_JOIN)
  const cachedGroupPassword = LocalStorage.get(
    LocalStorageKey.CACHED_GROUP_PASSWORD
  )
  LocalStorage.remove(LocalStorageKey.USER_ID)
  LocalStorage.clear()
  LocalStorage.set('i18nextLng', lang)
  LocalStorage.set(LocalStorageKey.CACHED_GROUP_ID_JOIN, cachedGroupId)
  LocalStorage.set(LocalStorageKey.CACHED_GROUP_PASSWORD, cachedGroupPassword)
  LocalStorage.set(LocalStorageKey.DEVICE_ID, deviceuuid)
  LocalStorage.set(LocalStorageKey.EXPIRY_DIALOG_SHOWN, expiryState)
  LocalStorage.set(LocalStorageKey.CODE_PROVIDER, provider)
  LocalStorage.set(LocalStorageKey.PROVIDER_OVERRIDE, providerOverride)
  LocalStorage.set(LocalStorageKey.THEME_MODE, themeMode)
  if (host !== null && host !== undefined)
    LocalStorage.set(LocalStorageKey.APP_HOST, host)
  if (tfmPass && isTestEnv()) {
    LocalStorage.set(LocalStorageKey.PASSWORD, tfmPass)
    if (!LocalStorage.get(LocalStorageKey.SHOW_DEV_INFO)) {
      LocalStorage.set(LocalStorageKey.SHOW_DEV_INFO, 1)
    }
  }

  if (isTestEnv() && !isCypress()) {
    // window.alert('Logout called; Erasing database')
  }

  // Defer database reset to allow all active observers/queries to complete
  // This prevents "Cannot call database.adapter while database is being reset" errors
  await new Promise((resolve) => {
    setTimeout(async () => {
      try {
        await Database.unsafeReset()
        console.log('Database cleared')
      } catch (error) {
        console.error('Database clear error:', error)
        // Continue with logout even if database reset fails
      }
      resolve(undefined)
    }, 100)
  })

  Analytics.getInstance().handleLogout()
}

export function useAuth(): AuthContextProps {
  return useContext(AuthContext)
}
